import React, { Suspense, useDeferredValue, useEffect, useState } from 'react';
import { UserGroup, Resource, Permission } from '@/types';
import { Shield, Plus, X, CheckSquare, Square, Loader2, Search } from 'lucide-react';
import { createId } from '@/lib/ids';
import { Pagination } from '@shared/components/common/Pagination';
import {
  useUserGroups,
  useUserGroupMutations,
  DataErrorBoundary,
  type UserGroupListParams
} from '@features/admin/data';

interface UserGroupsTabProps {
  canEdit: boolean;
  onPermissionsMayHaveChanged?: () => void;
}

const DEFAULT_PERMISSIONS: Record<Resource, Permission> = {
  reports: { view: false, edit: false },
  finance: { view: false, edit: false },
  bookings: { view: false, edit: false },
  customers: { view: false, edit: false },
  companies: { view: false, edit: false },
  staff: { view: false, edit: false },
  user_groups: { view: false, edit: false },
  services: { view: false, edit: false },
  venues: { view: false, edit: false },
  settings: { view: false, edit: false },
  questionnaires: { view: false, edit: false }
};

const mergeWithDefaultPermissions = (
  permissions?: Partial<Record<Resource, Partial<Permission>>>
): Record<Resource, Permission> => {
  const merged = { ...DEFAULT_PERMISSIONS };
  (Object.keys(DEFAULT_PERMISSIONS) as Resource[]).forEach((key) => {
    merged[key] = { ...DEFAULT_PERMISSIONS[key], ...(permissions?.[key] || {}) };
  });

  return merged;
};

const PermissionMatrix = ({
  permissions,
  onChange,
  disabled = false
}: {
  permissions: Record<Resource, Permission>;
  onChange: (p: Record<Resource, Permission>) => void;
  disabled?: boolean;
}) => {
  const resources: { key: Resource; label: string }[] = [
    { key: 'reports', label: 'Reports & Dashboard' },
    { key: 'finance', label: 'Finance & Invoicing' },
    { key: 'bookings', label: 'Bookings Management' },
    { key: 'customers', label: 'Customer Records' },
    { key: 'companies', label: 'Company Accounts' },
    { key: 'staff', label: 'Staff User Management' },
    { key: 'user_groups', label: 'User Group Management' },
    { key: 'services', label: 'Services & Add-ons' },
    { key: 'venues', label: 'Venues & Opening Hours' },
    { key: 'questionnaires', label: 'Questionnaires' },
    { key: 'settings', label: 'System Settings' }
  ];
  const normalizedPermissions = mergeWithDefaultPermissions(permissions);

  const togglePermission = (res: Resource, type: 'view' | 'edit') => {
    if (disabled) return;
    const current = normalizedPermissions[res];
    const updated = {
      ...normalizedPermissions,
      [res]: { ...current, [type]: !current[type] }
    };

    // Logic: If Edit is enabled, View must be enabled
    if (type === 'edit' && updated[res].edit) {
      updated[res].view = true;
    }
    // Logic: If View is disabled, Edit must be disabled
    if (type === 'view' && !updated[res].view) {
      updated[res].edit = false;
    }

    onChange(updated);
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 border-b">
          <tr>
            <th className="p-3 font-medium text-slate-600">Resource</th>
            <th className="p-3 font-medium text-slate-600 text-center w-24">View</th>
            <th className="p-3 font-medium text-slate-600 text-center w-24">Edit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {resources.map((res) => (
            <tr key={res.key} className="hover:bg-slate-50/50">
              <td className="p-3 font-medium text-slate-800">{res.label}</td>
              <td className="p-3 text-center">
                <button
                  onClick={() => togglePermission(res.key, 'view')}
                  disabled={disabled}
                  className={`p-1 rounded ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-100'}`}
                >
                  {normalizedPermissions[res.key]?.view ? (
                    <CheckSquare className="w-5 h-5 text-teal-600" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-300" />
                  )}
                </button>
              </td>
              <td className="p-3 text-center">
                <button
                  onClick={() => togglePermission(res.key, 'edit')}
                  disabled={disabled}
                  className={`p-1 rounded ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-100'}`}
                >
                  {normalizedPermissions[res.key]?.edit ? (
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Square className="w-5 h-5 text-slate-300" />
                  )}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UserGroupModal = ({
  group,
  onSave,
  onClose
}: {
  group: Partial<UserGroup>;
  onSave: (g: UserGroup) => void;
  onClose: () => void;
}) => {
  const [formData, setFormData] = useState<Partial<UserGroup>>({
    name: '',
    description: '',
    permissions: mergeWithDefaultPermissions(group.permissions),
    ...group
  });

  const isGlobalAdmin = formData.type === 'global_admin';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden animate-fade-in flex flex-col max-h-[90vh]">
        <div className="bg-slate-50 border-b p-4 flex justify-between items-center">
          <h3 className="font-bold text-lg text-slate-800">
            {group.id ? 'Edit User Group' : 'Create User Group'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-1">Group Name</label>
              <input
                className="w-full border rounded p-2 bg-white"
                value={formData.name}
                disabled={isGlobalAdmin}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <input
                className="w-full border rounded p-2 bg-white"
                value={formData.description}
                disabled={isGlobalAdmin}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>

          <h4 className="font-bold text-sm text-slate-700 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Permissions
            {isGlobalAdmin && (
              <span className="text-xs font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                Immutable
              </span>
            )}
          </h4>
          <PermissionMatrix
            permissions={formData.permissions as Record<Resource, Permission>}
            onChange={(p) => setFormData({ ...formData, permissions: p })}
            disabled={isGlobalAdmin}
          />
        </div>
        <div className="p-4 border-t flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded">
            Cancel
          </button>
          {!isGlobalAdmin && (
            <button
              onClick={() =>
                onSave({
                  ...formData,
                  id: formData.id || createId(),
                  type: formData.type || 'custom'
                } as UserGroup)
              }
              className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800"
            >
              Save Group
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const UserGroupsTab: React.FC<UserGroupsTabProps> = ({
  canEdit,
  onPermissionsMayHaveChanged
}) => {
  const groupMutations = useUserGroupMutations();
  const [editingUserGroup, setEditingUserGroup] = useState<UserGroup | null>(null);
  const [loadedGroups, setLoadedGroups] = useState<UserGroup[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <div className="animate-fade-in">
      {editingUserGroup && (
        <UserGroupModal
          group={editingUserGroup}
          onSave={async (g) => {
            if (loadedGroups.some((ug) => ug.id === g.id)) {
              await groupMutations.update(g);
            } else {
              await groupMutations.create(g);
            }
            setEditingUserGroup(null);
            onPermissionsMayHaveChanged?.();
          }}
          onClose={() => setEditingUserGroup(null)}
        />
      )}

      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-slate-800">User Groups & Permissions</h3>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search groups..."
              className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-56 bg-white text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canEdit && (
            <button
              onClick={() => setEditingUserGroup({} as UserGroup)}
              className="bg-slate-900 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Create Group
            </button>
          )}
        </div>
      </div>
      <DataErrorBoundary invalidateOnRetry="userGroups">
        <Suspense
          fallback={
            <div className="bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
              Loading user groups...
            </div>
          }
        >
          <UserGroupsListPanel
            page={page}
            pageSize={pageSize}
            search={deferredSearch}
            canEdit={canEdit}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            onEdit={setEditingUserGroup}
            onDelete={async (groupId) => {
              await groupMutations.remove(groupId);
              onPermissionsMayHaveChanged?.();
            }}
            onItemsLoaded={setLoadedGroups}
          />
        </Suspense>
      </DataErrorBoundary>
    </div>
  );
};

interface UserGroupsListPanelProps {
  page: number;
  pageSize: number;
  search: string;
  canEdit: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (group: UserGroup) => void;
  onDelete: (groupId: string) => Promise<void>;
  onItemsLoaded: (items: UserGroup[]) => void;
}

const UserGroupsListPanel: React.FC<UserGroupsListPanelProps> = ({
  page,
  pageSize,
  search,
  canEdit,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onItemsLoaded
}) => {
  const params: UserGroupListParams = {
    page,
    pageSize,
    q: search || undefined
  };
  const result = useUserGroups(params);
  const userGroups = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  useEffect(() => {
    onItemsLoaded(userGroups);
  }, [userGroups, onItemsLoaded]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {userGroups.length === 0 ? (
          <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500 italic">
            No user groups found.
          </div>
        ) : (
          userGroups.map((group) => {
            const isSystem = group.type === 'global_admin' || group.type === 'admin';
            const isImmutable = group.type === 'global_admin';
            const canEditGroup = canEdit && !isImmutable;

            return (
              <div
                key={group.id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-bold text-lg text-slate-900">{group.name}</h4>
                    <p className="text-xs text-slate-500 mt-1">{group.description}</p>
                  </div>
                  {isSystem && (
                    <Shield
                      className={`w-5 h-5 ${group.type === 'global_admin' ? 'text-purple-600' : 'text-blue-600'}`}
                    />
                  )}
                </div>

                <div className="flex-grow space-y-2 mb-6">
                  <p className="text-xs font-semibold text-slate-400 uppercase">
                    Access Highlights
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(group.permissions)
                      .filter(([_, p]) => (p as Permission).view)
                      .slice(0, 5)
                      .map(([key]) => (
                        <span
                          key={key}
                          className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs capitalize"
                        >
                          {key.replace('_', ' ')}
                        </span>
                      ))}
                    {Object.values(group.permissions).filter((p) => (p as Permission).view).length >
                      5 && (
                      <span className="bg-slate-50 text-slate-400 px-2 py-1 rounded text-xs">
                        + more
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 border-t pt-4">
                  {isImmutable ? (
                    <span className="text-xs text-slate-400 italic py-2">
                      System Group (Locked)
                    </span>
                  ) : (
                    <>
                      {canEditGroup ? (
                        <>
                          {group.type === 'custom' && (
                            <button
                              onClick={() => onDelete(group.id)}
                              className="text-red-600 hover:bg-red-50 px-3 py-1.5 rounded text-sm transition-colors"
                            >
                              Delete
                            </button>
                          )}
                          <button
                            onClick={() => onEdit(group)}
                            className="bg-slate-900 text-white px-4 py-1.5 rounded text-sm hover:bg-slate-800 transition-colors"
                          >
                            Edit Permissions
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => onEdit(group)}
                          className="text-slate-600 border px-4 py-1.5 rounded text-sm hover:bg-slate-50 transition-colors"
                        >
                          View Permissions
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={[25, 50, 100]}
      />
    </>
  );
};
